import { PayloadAction } from "@reduxjs/toolkit";
// REACT IMPORTS
import { useMemo } from "react";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// COMPONENT IMPORTS
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import { signAgreementWithRecovery } from "store/services/agreementSigning";
import {
  isAgreementPayPalOrder,
  isSavedForAgreement,
  shouldKeepAgreementSignature,
} from "store/services/agreementSignatureCache";
import { withCompanyLegalName } from "store/services/companyLegalName";
import {
  downloadAgreementFile,
  downloadPayment,
  getHistoryCount,
  getOrderDetails,
  getPaymentHistory,
  getPaymentLogo,
  getPaymentOffer,
  getPaymentPreviewData,
  getPaymentReceipt,
  getScheduledHistory,
  onCancelPaymentPlan,
  paymentAgreementData,
  resolveLegacyAgreementSlug,
  resolveLegacyUpdateBilling,
  paymentLinkData,
  processMakePayment,
  processPaymentLink,
  sendAgreementEmail,
  sendEmailRenewPaymentLink,
  updatePaymentPlan,
  createCashAppPI,
  downloadReceipt,
  getGeneralSettings,
  getPaymentHistoryDetails,
  getPreviewData,
  getScheduleID,
  googlePayMakePayment,
  paymentPermission,
  payPalCompleteOneTime,
  payPalIntent,
  productLinkPaymnet,
  quickCheckoutPayment,
  verifyWalletPayments,
  WirePayment,
  getConvFee,
  getPaymentMarkers,
  getCompanyMarkers,
  sendEmailRenewAgreement,
  makeOneTimePayment,
  createpaypalToken,
  createpaypalPaymentToken,
  createPlaidLinkToken,
  createPlaidPaymentToken,
  getDuplicatePayment,
  downloadReceiptPdf,
  downloadAgreementReceiptPdf,
  markIncompleteOrderFailed,
  verifyStaxPayment,
  getReceiptID,
  sendPaymentAssistance,
  sendScheduleUpdatedEmail,
} from "store/services/paymentServices";
import type { LegacyUpdateBillingResolverArgs } from "utils/legacyUpdateBillingRoute";
import {
  getBrowserTimezoneName,
  getBrowserTimezoneOffset,
} from "utils/HelperFunctions";
import type { StripeElements } from "@stripe/stripe-js";
import {
  PaymentCountsResponse,
  PaymentCountsResponseKeys,
  PaymentHistoryCount,
} from "types/payments";
import { ScheduledData, ScheduleHistoryCount } from "types/scheduled";
import type { ScheduleUpdatedEmailPayload } from "types/updatePaymentPlan";

interface InitialState {
  historyCount: PaymentHistoryCount | null;
  paymentHistory: any;
  scheduleHistoryCount: ScheduleHistoryCount | null;
  scheduleHistory: ScheduledData | null;
  paymentReceipt: any;
  orderDetails: any;
  paymentOffer: any;
  paymentLogo: any;
  favicon: any;
  isLogoLoading: any;
  isCompanyDetailsLoading: any;
  companyDetails: any;
  clientSecret: any;
  paymentPermission: any;
  paymentPlan: any;
  payPalOrder: any;
  agreementSign: any;
  generalSettings: any;
  minAmount: number;
  maxAmount: number;
  isFeeLoading: any;
  conFee: number;
  activeConvFeeRequestId: string | null;
  paymentMarkers: any;
  companyMarkers: any;
  cardValues: any;
  paymentElement: StripeElements | null;
  duplicateOrdersCount: number;
  isDuplicateOrders: any;
  checkedDuplicatePayment: any;
  phoneNumber: string;
  isPhoneSelected: boolean;
  isDetectingCountry: boolean;
  isPaymentProcessing: boolean;
}

const initialState: InitialState = {
  historyCount: null,
  paymentHistory: {},
  scheduleHistoryCount: null,
  scheduleHistory: null,
  paymentReceipt: {},
  orderDetails: {},
  paymentOffer: {},
  isLogoLoading: false,
  isCompanyDetailsLoading: false,
  paymentLogo: "",
  favicon: "",
  companyDetails: {},
  clientSecret: undefined,
  paymentPermission: {},
  paymentPlan: {},
  payPalOrder: undefined,
  agreementSign: undefined,
  generalSettings: {},
  minAmount: 0,
  maxAmount: 0,
  isFeeLoading: false,
  conFee: 0.0,
  activeConvFeeRequestId: null,
  paymentMarkers: {},
  companyMarkers: {},
  cardValues: {},
  paymentElement: null,
  duplicateOrdersCount: 0,
  isDuplicateOrders: false,
  checkedDuplicatePayment: false,
  phoneNumber: "",
  isPhoneSelected: false,
  isDetectingCountry: false,
  isPaymentProcessing: false,
};

const withCustomerTimezone = (params: any) => ({
  ...params,
  timezone: getBrowserTimezoneOffset(),
  customer_timezone: getBrowserTimezoneName(),
});

export const historyCountGet = createAsyncThunk(
  "history-count/get",
  async (params: any) => {
    return await getHistoryCount(params);
  }
);

export const paymentHistoryGet = createAsyncThunk(
  "payment-history/get",
  async (params: any) => {
    return await getPaymentHistory(params);
  }
);

export const paymentScheduledGet = createAsyncThunk(
  "scheduled-history/get",
  async (params: any) => {
    return await getScheduledHistory(params);
  }
);

export const paymentReceiptGet = createAsyncThunk(
  "payment-receipt/get",
  async (params: any) => {
    return await getPaymentReceipt(params);
  }
);

export const orderDetailsGet = createAsyncThunk(
  "order-details/get",
  async (params: any) => {
    return await getOrderDetails(params);
  }
);

export const paymentLogoGet = createAsyncThunk(
  "payment-logo/get",
  async (params: any) => {
    return await getPaymentLogo(params);
  }
);

export const previewDataGet = createAsyncThunk(
  "preview-data/get",
  async (params: any) => {
    return await getPreviewData(params);
  }
);
export const updatePayment = createAsyncThunk(
  "payment-plan/update",
  async (params: any) => {
    return await updatePaymentPlan(params);
  }
);

export const sendPaymentPlanUpdatedEmail = createAsyncThunk(
  "payment-plan/send-schedule-updated-email",
  async (payload: ScheduleUpdatedEmailPayload) => {
    return await sendScheduleUpdatedEmail(payload);
  }
);

export const makeGooglePay = createAsyncThunk(
  "google-pay-make-payment/pay",
  async (payload: any) => {
    return await googlePayMakePayment(payload);
  }
);

export const oneTimeMakePayment = createAsyncThunk(
  "make-one-time-payment/post",
  async (payload: any) => {
    return await makeOneTimePayment(payload);
  }
);

const previwePaymentPlan = createAsyncThunk(
  "payment-plan/update",
  async (params: any) => {
    return await getPaymentPreviewData(params);
  }
);

export const paymentDownload = createAsyncThunk(
  "payment-slip/download",
  async (payload: any) => {
    return await downloadPayment(payload);
  }
);

export const paymentReceiptDownload = createAsyncThunk(
  "payment-receipt/download",
  async (payload: any) => {
    return await downloadReceipt(payload);
  }
);

export const paymentReceiptDownloadPdf = createAsyncThunk(
  "payment-receipt/download-pdf",
  async (params: any) => {
    return await downloadReceiptPdf(withCustomerTimezone(params));
  }
);

export const paymentReceiptAgreementDownloadPdf = createAsyncThunk(
  "payment-receipt/agreement-download-pdf",
  async (params: any) => {
    return await downloadAgreementReceiptPdf(withCustomerTimezone(params));
  }
);

export const receiptIdGet = createAsyncThunk(
  "payment-receipt/get-receipt-id",
  async (params: any) => {
    return await getReceiptID(params);
  }
);

export const cancelPaymentPlan = createAsyncThunk(
  "payment-slip/download",
  async (payload: any) => {
    return await onCancelPaymentPlan(payload);
  }
);

export const getPaymentLink = createAsyncThunk(
  "payment-link/data",
  async (payload: any) => {
    return await paymentLinkData(payload);
  }
);

export const sendEmailRenew = createAsyncThunk(
  "payment-link/email-renew",
  async (payload: any) => {
    return await sendEmailRenewPaymentLink(payload);
  }
);

export const sendAgreementRenew = createAsyncThunk(
  "agreement-email/renew",
  async (payload: any) => {
    return await sendEmailRenewAgreement(payload);
  }
);

export const processLink = createAsyncThunk(
  "payment-link/process",
  async (payload: any) => {
    return await processPaymentLink(payload);
  }
);

export const wirePaymentProcess = createAsyncThunk(
  "payment-wire/post",
  async (payload: any) => {
    return await WirePayment(payload);
  }
);

export const payPalOneTimeCreate = createAsyncThunk(
  "pay-pal-one-time/create",
  async (payload: any) => {
    return await payPalIntent(payload);
  }
);

export const payPalOneTimeExecute = createAsyncThunk(
  "pay-pal-one-time/execute",
  async (params: any) => {
    return await payPalCompleteOneTime(params);
  }
);

export const makePayment = createAsyncThunk(
  "payment-link/process",
  async (payload: any) => {
    return await processMakePayment(payload);
  }
);

export const getPaymentAgreementData = createAsyncThunk(
  "payment-agreement/data",
  async (params: any) => {
    return await paymentAgreementData(params);
  }
);

export const getLegacyAgreementSlug = createAsyncThunk(
  "payment-agreement/resolve-legacy-slug",
  async (params: { slug: string }) => {
    return await resolveLegacyAgreementSlug(params);
  }
);

export const getLegacyUpdateBillingCluster = createAsyncThunk(
  "legacy-update-billing/resolve-cluster",
  async (payload: LegacyUpdateBillingResolverArgs) => {
    return await resolveLegacyUpdateBilling(payload);
  }
);

export const signAgreement = createAsyncThunk(
  "payment-agreement/process",
  async (payload: any, { getState }) => {
    // Every agreement payment path (card, bank, wallets, PayPal return) sends
    // the E-SIGN consent time captured with the signature, when there is one.
    const consentAt = (getState() as any)?.payment?.agreementSign
      ?.esign_consent_at;
    return await signAgreementWithRecovery(
      consentAt && !payload?.esign_consent_at
        ? { ...payload, esign_consent_at: consentAt }
        : payload
    );
  }
);

export const paymentOfferGet = createAsyncThunk(
  "payment-offer/get",
  async (params: any) => {
    return await getPaymentOffer(params);
  }
);

export const agreementEmail = createAsyncThunk(
  "agreemnet-email/send",
  async (payload: any) => {
    return await sendAgreementEmail(withCustomerTimezone(payload));
  }
);

const fetchScheduleID = createAsyncThunk(
  "agreemnet-email/schedule-id",
  async (params: any) => {
    return await getScheduleID(params);
  }
);

export const fetchPaymentDetails = createAsyncThunk(
  "payment-details/get",
  async (params: any) => {
    return await getPaymentHistoryDetails(params);
  }
);

export const getpaymentPermission = createAsyncThunk(
  "payment-permission/post",
  async (payload: any) => {
    return await paymentPermission(payload);
  }
);

export const productLinkPay = createAsyncThunk(
  "product-link-card-bank/payment",
  async (payload: any) => {
    return await productLinkPaymnet(payload);
  }
);

export const quickCheckoutPay = createAsyncThunk(
  "quick-checkout/payment",
  async (payload: any) => {
    return await quickCheckoutPayment(payload);
  }
);

const prepareData = (data: PaymentCountsResponse): PaymentHistoryCount => {
  let nData = data?.data;

  let total = 0;
  for (const key in nData) {
    if (nData.hasOwnProperty(key)) {
      total += nData[key as keyof PaymentCountsResponseKeys] || 0;
    }
  }
  let fData = { ...data?.data, all: total };

  let Succeeded = (fData["Succeeded"] || 0) + (fData["DisputedWon"] || 0) || 0;

  let Failed =
    (fData["Failed"] || 0) +
      (fData["Disputed"] || 0) +
      (fData["Voided"] || 0) +
      (fData["DisputedLoss"] || 0) || 0;

  let refundCount =
    (fData["Refunded"] || 0) + (fData["PartiallyRefunded"] || 0) || 0;

  return { all: total, refundCount, Succeeded, Failed };
};

const prepareScheduleData = (payload: any) => {
  const schedulePaymentData = payload?.data["schedulePayment"];
  let nData = schedulePaymentData?.map((x: any) => {
    return {
      ...x,
      id: x.custom_schedule_id,
      custom_schedule_id: x.id,
    };
  });
  return { schedulePayment: nData, totalCount: payload?.data["totalCount"] };
};

const prepareScheduleCount = (payload: any): ScheduleHistoryCount => {
  const restData = {
    currentMonthCount: payload?.data["currentMonthCount"] || 0,
    nextMonthCount: payload?.data["nextMonthCount"] || 0,
    next3Month: payload?.data["next3Month"] || 0,
    AllMonthCount: payload?.data["AllMonthCount"] || 0,
  };

  return { ...restData };
};

export const getIntent = createAsyncThunk(
  "cashapp-pi/create",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await createCashAppPI(payload);
    } catch (error: any) {
      // Non-2xx responses carry { status, message, body: { success, data: { msg } } }.
      // Keep that body so callers can show the server's reason instead of losing it.
      if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      }
      throw error;
    }
  }
);

export const getPlaidLinkToken = createAsyncThunk(
  "plaid-link-token/get",
  async (payload: any) => {
    return await createPlaidLinkToken(payload);
  }
);

export const getPlaidPaymentToken = createAsyncThunk(
  "plaid-paymnet-token/get",
  async (payload: any) => {
    return await createPlaidPaymentToken(payload);
  }
);

export const createPaypalToken = createAsyncThunk(
  "paypal-pi/create",
  async (payload: any) => {
    return await createpaypalToken(payload);
  }
);

export const createPaypalPaymentToken = createAsyncThunk(
  "paypal-payment-pi/create",
  async (payload: any) => {
    return await createpaypalPaymentToken(payload);
  }
);

export const verifyWalletPay = createAsyncThunk(
  "verify-wallet/pay",
  async (payload: any) => {
    return await verifyWalletPayments(payload);
  }
);

export const generalSettingsGet = createAsyncThunk(
  "general-settings/get",
  async (params: any) => {
    return await getGeneralSettings(params);
  }
);

export const convFeeGet = createAsyncThunk(
  "conv-fee/get",
  async (params: any) => {
    return await getConvFee(params);
  }
);

export const markIncompletePayment = createAsyncThunk(
  "mark-incomplete-order/post",
  async (payload: any) => {
    return await markIncompleteOrderFailed(payload);
  }
);

export const verifyPaymentsViaStax = createAsyncThunk(
  "verify-stax-payment/post",
  async (payload: any) => {
    return await verifyStaxPayment(payload);
  }
);

export const paymentMarkersGet = createAsyncThunk(
  "payment-markers/get",
  async (params: any) => {
    return await getPaymentMarkers(params);
  }
);

export const companyMarkersGet = createAsyncThunk(
  "company-markers/get",
  async (params: any) => {
    return await getCompanyMarkers(params);
  }
);

export const sendPaymentAssistanceRequest = createAsyncThunk(
  "payment-assistance/send",
  async (payload: any) => {
    return await sendPaymentAssistance(payload);
  }
);

export const duplicatePaymentGet = createAsyncThunk(
  "duplicate-payment/get",
  async (params: any) => {
    return await getDuplicatePayment(params);
  }
);

/* ------------------------------------- PAYMENT SLICE ------------------------------------- */

const paymentsSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    // Wipes the slice on logout and on session timeout. Everything here is
    // per-customer and is persisted to the browser, so anything left behind is
    // shown to the next person who signs in on this device — the agreement
    // signature was being re-displayed to a different account that way.
    // Branding is the only exception: the logo, favicon and company details
    // dress the login screen and are not customer data.
    clearPaymentsSlice: (state) => {
      const preserved: (keyof typeof initialState)[] = [
        "paymentLogo",
        "favicon",
        "companyDetails",
      ];
      for (const key in initialState) {
        if (preserved.includes(key as keyof typeof initialState)) {
          continue;
        }
        if (initialState.hasOwnProperty(key)) {
          const typedKey = key as keyof typeof initialState;
          (state[typedKey] as any) = initialState[typedKey];
        }
      }
    },
    storePaymnetPlan: (state, action) => {
      state.paymentPlan = action.payload;
    },
    storePayPalOrder: (state, action) => {
      state.payPalOrder = action.payload;
    },
    storeAgreementSignature: (state, action) => {
      state.agreementSign = action.payload;
    },
    // E-SIGN consent is saved as soon as it is given, so every copy of the
    // signature step reads the same value and closing the wizard keeps it. A
    // signature already saved for this agreement stays; one for another
    // agreement is replaced.
    storeAgreementConsent: (
      state,
      action: PayloadAction<{ slug: string; esign_consent_at: string }>
    ) => {
      const { slug, esign_consent_at } = action.payload;
      state.agreementSign = isSavedForAgreement(state.agreementSign, slug)
        ? { ...state.agreementSign, slug, esign_consent_at }
        : { slug, esign_consent_at };
    },
    // The signature is only meaningful inside the agreement flow that captured
    // it. It is persisted to the browser, so leaving that page has to drop it —
    // otherwise it is re-displayed, already signed, on the next agreement.
    clearAgreementSignature: (state) => {
      state.agreementSign = undefined;
    },
    // Runs once after rehydration. The signature (and the PayPal order, which
    // carries its own copy) only has to survive a payment provider's redirect
    // back to the agreement it was drawn for; any other load drops it.
    dropStaleAgreementSignature: (
      state,
      action: PayloadAction<{ pathname: string; search: string }>
    ) => {
      if (!shouldKeepAgreementSignature(state.agreementSign, action.payload)) {
        state.agreementSign = undefined;
      }
      if (
        isAgreementPayPalOrder(state.payPalOrder) &&
        !shouldKeepAgreementSignature(state.payPalOrder, action.payload)
      ) {
        state.payPalOrder = undefined;
      }
    },
    storeCardValues: (state, action) => {
      state.cardValues = action.payload;
    },
    storePaymentElement: (state, action) => {
      state.paymentElement = action.payload;
    },
    updateLogoAndFavicon: (state, action) => {
      state.paymentLogo = action.payload.logo || "";
      state.favicon = action.payload.favicon || "";
      // The payload only has the display name; it is the fallback for when
      // no markers response has given the legal name.
      state.paymentMarkers = withCompanyLegalName({
        paymentMarkers: state.paymentMarkers,
        companyMarkers: state.companyMarkers,
        fallbackName: action.payload?.company_name || action.payload?.name,
      });
    },
    updateCompanyDetails: (state, action) => {
      state.companyDetails = action.payload;
      state.paymentMarkers = withCompanyLegalName({
        paymentMarkers: state.paymentMarkers,
        companyMarkers: state.companyMarkers,
        fallbackName: action.payload?.company_name || action.payload?.name,
      });
    },
    updatePaymentPermission: (state, action) => {
      state.paymentPermission = action.payload;
    },
    resetDuplicateOrder: (state) => {
      state.isDuplicateOrders = false;
      state.duplicateOrdersCount = 0;
    },
    resetPaymentConfigs: (state) => {
      state.paymentElement = null;
      state.conFee = 0.0;
      state.isFeeLoading = false;
      state.activeConvFeeRequestId = null;
      // state.agreementSign = undefined;
    },
    resetCheckDuplicatePayment: (state) => {
      state.checkedDuplicatePayment = false;
    },
    resetLogoOnRefresh: (state) => {
      state.companyDetails = {};
    },
    resetConvFee: (state) => {
      state.conFee = 0.0;
      state.isFeeLoading = false;
      state.activeConvFeeRequestId = null;
    },
    setPhoneNumber: (state, action) => {
      state.phoneNumber = action.payload;
    },
    setIsPhoneSelected: (state, action) => {
      state.isPhoneSelected = action.payload;
    },
    setIsDetectingCountry: (state, action) => {
      state.isDetectingCountry = action.payload;
    },
    setIsPaymentProcessing: (state, action) => {
      state.isPaymentProcessing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      historyCountGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.historyCount = prepareData(action.payload?.body);
      }
    );

    builder.addCase(
      paymentHistoryGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.paymentHistory = action.payload?.body?.data || {};
      }
    );

    builder.addCase(
      paymentReceiptGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.paymentReceipt = action.payload?.body?.data || {};
      }
    );

    builder.addCase(
      orderDetailsGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.orderDetails = action.payload?.body?.data || {};
      }
    );

    builder.addCase(
      paymentScheduledGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.scheduleHistoryCount = prepareScheduleCount(action.payload?.body);
        state.scheduleHistory = prepareScheduleData(action.payload?.body);
      }
    );

    builder.addCase(
      paymentOfferGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.paymentOffer = action.payload?.body?.data || {};
      }
    );
    builder.addCase(
      paymentLogoGet.pending,
      (state, action: PayloadAction<any>) => {
        state.isLogoLoading = true;
      }
    );
    builder.addCase(
      paymentLogoGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.paymentLogo = action.payload.body?.data?.logo || "";
        state.favicon = action.payload.body?.data?.favicon || "";
        state.isLogoLoading = false;
      }
    );
    builder.addCase(
      previewDataGet.pending,
      (state, action: PayloadAction<any>) => {
        state.isCompanyDetailsLoading = true;
      }
    );
    builder.addCase(
      previewDataGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload.body?.success) {
          state.companyDetails = action.payload.body?.data?.[0] || {};
          state.isCompanyDetailsLoading = false;
        }
      }
    );

    builder.addCase(
      getIntent.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action?.payload?.success) {
          const { data } = action?.payload;
          if (data?.client_secret) {
            state.clientSecret = data.client_secret;
          }
        }
      }
    );

    builder.addCase(
      getpaymentPermission.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (
          action?.payload?.status === 200 ||
          action?.payload?.status === 201
        ) {
          const { body } = action?.payload;
          state.paymentPermission = body;
        }
      }
    );

    builder.addCase(
      generalSettingsGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload.body.success) {
          state.generalSettings = action.payload.body.data;
        }
      }
    );
    builder.addCase(convFeeGet.pending, (state, action) => {
      state.activeConvFeeRequestId = action.meta.requestId;
      state.conFee = 0;
      state.isFeeLoading = true;
    });
    builder.addCase(convFeeGet.rejected, (state, action) => {
      if (state.activeConvFeeRequestId !== action.meta.requestId) return;

      state.conFee = 0;
      state.isFeeLoading = false;
      state.activeConvFeeRequestId = null;
    });
    builder.addCase(convFeeGet.fulfilled, (state, action) => {
      if (state.activeConvFeeRequestId !== action.meta.requestId) return;

      // A fulfilled request can still contain an unsuccessful API response.
      // Always clear the pending state so it cannot leave payment actions disabled.
      state.isFeeLoading = false;
      state.activeConvFeeRequestId = null;
      if (action?.payload?.body?.success) {
        const fee = Number(action.payload.body.data?.fee ?? 0);
        state.conFee = Number.isFinite(fee) ? fee : 0;
      } else {
        state.conFee = 0;
      }
    });
    builder.addCase(
      paymentMarkersGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action?.payload?.body?.success) {
          const markers = action?.payload?.body?.data;
          state.paymentMarkers = withCompanyLegalName({
            paymentMarkers: markers,
            companyMarkers: state.companyMarkers,
            fallbackName:
              markers?.["Company: Name"] ||
              state.paymentMarkers?.["Company Legal Name"],
          });
        }
      }
    );
    builder.addCase(
      companyMarkersGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action?.payload?.body?.success) {
          state.companyMarkers = action?.payload?.body?.data;
          state.paymentMarkers = withCompanyLegalName({
            paymentMarkers: state.paymentMarkers,
            companyMarkers: state.companyMarkers,
            fallbackName: state.paymentMarkers?.["Company Legal Name"],
          });
        }
      }
    );
    builder.addCase(
      duplicatePaymentGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isDuplicateOrders =
          action?.payload?.body?.data?.isDuplicateOrders || false;
        state.duplicateOrdersCount =
          action?.payload?.body?.data?.duplicateOrdersCount || 0;
        state.checkedDuplicatePayment = true;
      }
    );
    // Once the agreement is signed the saved signature has done its job. A
    // failed attempt keeps it: "Update payment method" retries from the
    // payment step, possibly through another redirect, and still needs it.
    builder.addCase(
      signAgreement.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action?.payload?.body?.success) {
          state.agreementSign = undefined;
          if (isAgreementPayPalOrder(state.payPalOrder)) {
            state.payPalOrder = undefined;
          }
        }
      }
    );
  },
});

const {
  clearPaymentsSlice,
  clearAgreementSignature,
  dropStaleAgreementSignature,
  storePaymnetPlan,
  storePayPalOrder,
  storeAgreementSignature,
  storeAgreementConsent,
  storeCardValues,
  storePaymentElement,
  updateLogoAndFavicon,
  updatePaymentPermission,
  updateCompanyDetails,
  resetDuplicateOrder,
  resetPaymentConfigs,
  resetCheckDuplicatePayment,
  resetLogoOnRefresh,
  resetConvFee,
  setPhoneNumber,
  setIsPhoneSelected,
  setIsDetectingCountry,
  setIsPaymentProcessing,
} = paymentsSlice.actions;
export {
  clearPaymentsSlice,
  clearAgreementSignature,
  dropStaleAgreementSignature,
  storePaymnetPlan,
  storePayPalOrder,
  storeAgreementSignature,
  storeAgreementConsent,
  storeCardValues,
  storePaymentElement,
  updateLogoAndFavicon,
  updatePaymentPermission,
  updateCompanyDetails,
  resetDuplicateOrder,
  resetPaymentConfigs,
  resetCheckDuplicatePayment,
  resetLogoOnRefresh,
  resetConvFee,
  setIsPaymentProcessing,
};
export default paymentsSlice.reducer;

const selectHistoryCount = (state: RootState) => state.payment.historyCount;
export const useHistoryCount = () => {
  const historyCount = useAppSelector(selectHistoryCount);
  return useMemo(() => historyCount, [historyCount]);
};

const selectPaymentHistory = (state: RootState) => state.payment.paymentHistory;
export const usePaymentHistory = () => {
  const paymentHistory = useAppSelector(selectPaymentHistory);
  return useMemo(() => paymentHistory, [paymentHistory]);
};

const selectPaymentReceipt = (state: RootState) => state.payment.paymentReceipt;
const usePaymentReceipt = () => {
  const paymentReceipt = useAppSelector(selectPaymentReceipt);
  return useMemo(() => paymentReceipt, [paymentReceipt]);
};

const selectOrderDetails = (state: RootState) => state.payment.orderDetails;
const useOrderDetails = () => {
  const orderDetails = useAppSelector(selectOrderDetails);
  return useMemo(() => orderDetails, [orderDetails]);
};

const selectScheduleHistoryCount = (state: RootState) =>
  state.payment.scheduleHistoryCount;
export const useScheduleHistoryCount = () => {
  const scheduleHistoryCount = useAppSelector(selectScheduleHistoryCount);
  return useMemo(() => scheduleHistoryCount, [scheduleHistoryCount]);
};

const selectScheduleHistory = (state: RootState) =>
  state.payment.scheduleHistory;
export const useScheduleHistory = () => {
  const scheduleHistory = useAppSelector(selectScheduleHistory);
  return useMemo(() => scheduleHistory, [scheduleHistory]);
};

const selectPaymentOffer = (state: RootState) => state.payment.paymentOffer;
const usePaymentOffer = () => {
  const paymentOffer = useAppSelector(selectPaymentOffer);
  return useMemo(() => paymentOffer, [paymentOffer]);
};

const selectPaymentLogo = (state: RootState) => state.payment.paymentLogo;
const usePaymentLogo = () => {
  const paymentLogo = useAppSelector(selectPaymentLogo);
  return useMemo(() => paymentLogo, [paymentLogo]);
};
